import React from 'react';
import { Button, Menu, MenuItem, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

const LanguageButton = styled(Button)(({ theme }) => ({
  color: '#6B7280',
  fontSize: '0.95rem',
  fontWeight: 500,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  minWidth: 'auto',
  gap: theme.spacing(1),
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(67, 56, 202, 0.04)',
  },
}));

const FlagIcon = styled('span')({
  fontSize: '1.2rem',
  lineHeight: '1',
});

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    handleClose();
  };

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language.split('-')[0]) || languages[0];

  return (
    <Box component="nav" role="navigation" aria-label="Language selection">
      <LanguageButton
        onClick={handleClick}
        endIcon={<ExpandMoreIcon />}
        startIcon={<FlagIcon>{currentLanguage.flag}</FlagIcon>}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? 'language-menu' : undefined}
        aria-label={`Current language: ${currentLanguage.name}. Click to change language`}
      >
        {currentLanguage.name}
      </LanguageButton>
      
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': 'language-menu',
          role: 'menu',
        }}
        PaperProps={{
          sx: {
            minWidth: 120,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: '1px solid #E5E7EB',
          },
          role: 'navigation',
          'aria-label': 'Language options',
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            selected={i18n.language === lang.code}
            role="menuitem"
            aria-label={`Switch to ${lang.name}`}
            sx={{
              gap: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(67, 56, 202, 0.08)',
              },
            }}
          >
            <FlagIcon>{lang.flag}</FlagIcon>
            <Typography variant="body2">{lang.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher; 